/** Tiny path router (history API). */
export type Route =
  | { name: 'home' }
  | { name: 'login' }
  | { name: 'callback' }
  | { name: 'set-password' }
  | { name: 'availability'; month?: string }
  | { name: 'team' }
  | { name: 'person'; key: string }
  | { name: 'settings-me' }
  | { name: 'settings-modules' }
  | { name: 'settings-connections' }
  | { name: 'settings-workload' }
  | { name: 'settings-email' }
  | { name: 'settings-whats-new' }
  | { name: 'news' }
  | { name: 'my-time'; week?: string }
  | { name: 'news-post'; id: string }
  | { name: 'news-new' }
  | { name: 'news-edit'; id: string }
  | { name: 'people' }
  | { name: 'people-invite' }
  | { name: 'people-person'; id: string }
  | { name: 'projects' }
  | { name: 'project'; id: string; tab: 'videos' | 'brief' | 'shoots' | 'actors' | 'notes' | 'files' | 'activity' | 'details' }
  /** two projects side by side (owner 2026-11-02, for editors): /projects/{a}/side/{b}?a=brief&b=videos */
  | { name: 'side'; a: string; b: string; ta: PaneTab; tb: PaneTab }
  | { name: 'review'; projectId: string; videoId: string; version: string | null }
  | { name: 'shoot-prep'; projectId: string; shootId: string }
  | { name: 'actor-release'; id: string; cid: string }
  | { name: 'sign'; token: string }
  | { name: 'esign'; token: string }
  | { name: 'esign-verify' }
  | { name: 'talent'; tab: 'library' | 'applications' }
  | { name: 'apply'; ad: string | null }
  | { name: 'casting-answer'; token: string }
  | { name: 'clients'; tab: 'companies' | 'contacts' }
  | { name: 'client'; id: string }
  | { name: 'agreements' }
  | { name: 'deals' }
  | { name: 'contracts' }
  | { name: 'finance'; tab: 'overview' | 'invoicing' | 'costs' | 'margins' | 'customers' | 'terms' }
  | { name: 'agreement'; id: string | null }
  | { name: 'agreement-doc'; id: string; part?: 'contract' }
  | { name: 'price-list' }
  | { name: 'agreement-templates' }
  | { name: 'tickets' }
  | { name: 'archive' }
  | { name: 'post'; tab: 'overview' | 'guide' | 'tools' | 'accounts' | 'tips'; open: string | null }
  | { name: 'guide' }
  | { name: 'link'; token: string }
  | { name: 'crew-signin' }
  | { name: 'portal-link'; token: string }
  | { name: 'portal-signin' }
  | { name: 'portal'; code: string | null }
  | { name: 'portal-review'; code: string }
  | { name: 'legal'; doc: 'privacy' | 'terms' }
  | { name: 'not-found' };

/** The tabs of one side in "side by side": what an editor needs to do a good job (owner 2026-11-02). */
export type PaneTab = 'brief' | 'videos' | 'files' | 'shoot';
export const PANE_TABS: PaneTab[] = ['brief', 'videos', 'files', 'shoot'];
const paneTab = (x: string | null): PaneTab => (PANE_TABS as string[]).includes(x ?? '') ? (x as PaneTab) : 'brief';
/** The address of two projects side by side; the tabs only when they are not the brief. */
export function sidePath(a: string, b: string, ta: PaneTab = 'brief', tb: PaneTab = 'brief'): string {
  const q = new URLSearchParams();
  if (ta !== 'brief') q.set('a', ta);
  if (tb !== 'brief') q.set('b', tb);
  const qs = q.toString();
  return `/projects/${encodeURIComponent(a)}/side/${encodeURIComponent(b)}${qs ? `?${qs}` : ''}`;
}

export function parseRoute(pathname: string, search = ''): Route {
  const q = new URLSearchParams(search);
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean).map(decodeURIComponent);
  const [a, b, c] = parts;
  if (!a) return { name: 'home' };
  if (a === 'login') return { name: 'login' };
  // public privacy policy and terms of service, no sign-in (owner 2026-11-02)
  if ((a === 'privacy' || a === 'terms') && !b) return { name: 'legal', doc: a };
  if (a === 'auth' && b === 'callback') return { name: 'callback' };
  if (a === 'auth' && b === 'password') return { name: 'set-password' };
  // Team's tabs: Capacity (/team), Availability (/team/availability), People (/team/people); the old paths still work
  if ((a === 'team' && b === 'availability' && !c) || (a === 'availability' && !b)) return { name: 'availability', month: q.get('month') ?? undefined };
  if ((a === 'team' && b === 'people' && !c) || (a === 'people' && !b)) return { name: 'people' };
  if (a === 'team' && b) return { name: 'person', key: b };
  if (a === 'team') return { name: 'team' };
  if (a === 'settings' && !b) return { name: 'settings-me' };
  if (a === 'settings' && b === 'modules') return { name: 'settings-modules' };
  if (a === 'settings' && b === 'connections') return { name: 'settings-connections' };
  if (a === 'settings' && b === 'workload') return { name: 'settings-workload' };
  if (a === 'settings' && b === 'email') return { name: 'settings-email' };
  if (a === 'settings' && b === 'whats-new') return { name: 'settings-whats-new' };
  if (a === 'news' && b === 'new') return { name: 'news-new' };
  if (a === 'news' && b && c === 'edit') return { name: 'news-edit', id: b };
  if (a === 'news' && b) return { name: 'news-post', id: b };
  if (a === 'news') return { name: 'news' };
  if (a === 'guide' && !b) return { name: 'guide' };
  if (a === 'my-time') return { name: 'my-time', week: q.get('week') ?? undefined };
  if (a === 'people' && b === 'invite') return { name: 'people-invite' };
  if (a === 'people' && b) return { name: 'people-person', id: b };
  if (a === 'projects' && b && c === 'review' && parts[3] && !parts[4]) return { name: 'review', projectId: b, videoId: parts[3], version: q.get('version') };
  // "Inför inspelningen" (owner 2026-11-01): one shoot on one page
  if (a === 'projects' && b && c === 'shoots' && parts[3] && !parts[4]) return { name: 'shoot-prep', projectId: b, shootId: parts[3] };
  if (a === 'projects' && b && c === 'side' && parts[3] && !parts[4]) return { name: 'side', a: b, b: parts[3], ta: paneTab(q.get('a')), tb: paneTab(q.get('b')) };
  if (a === 'projects' && b && c === 'actors' && parts[3] && parts[4] === 'release') return { name: 'actor-release', id: b, cid: parts[3] };
  if (a === 'projects' && b && (!c || c === 'brief' || c === 'shoots' || c === 'actors' || c === 'notes' || c === 'files' || c === 'activity' || c === 'details')) return { name: 'project', id: b, tab: (c as 'brief' | 'shoots' | 'actors' | 'notes' | 'files' | 'activity' | 'details' | undefined) ?? 'videos' };
  if (a === 'projects' && !b) return { name: 'projects' };
  if (a === 'clients' && b === 'contacts' && !c) return { name: 'clients', tab: 'contacts' };
  if (a === 'clients' && b && !c) return { name: 'client', id: b };
  if (a === 'clients' && !b) return { name: 'clients', tab: 'companies' };
  if (a === 'agreements' && b === 'price-list') return { name: 'price-list' };
  if (a === 'agreements' && b === 'templates') return { name: 'agreement-templates' };
  if (a === 'agreements' && b === 'board') return { name: 'deals' };
  if (a === 'contracts' && !b) return { name: 'contracts' };
  if (a === 'finance' && !b) return { name: 'finance', tab: 'overview' };
  if (a === 'finance' && (b === 'invoicing' || b === 'costs' || b === 'margins' || b === 'customers' || b === 'terms') && !c) return { name: 'finance', tab: b };
  if (a === 'agreements' && b === 'new') return { name: 'agreement', id: null };
  if (a === 'agreements' && b && c === 'document') return { name: 'agreement-doc', id: b };
  if (a === 'agreements' && b && c === 'contract') return { name: 'agreement-doc', id: b, part: 'contract' };
  if (a === 'agreements' && b && !c) return { name: 'agreement', id: b };
  if (a === 'agreements' && !b) return { name: 'agreements' };
  if (a === 'tickets' && !b) return { name: 'tickets' };
  if (a === 'archive' && !b) return { name: 'archive' };
  // Post production for Redigering and Kamera (owner 2026-11-03): the guide, and the shared tools and files
  if (a === 'post' && !b) return { name: 'post', tab: 'overview', open: null };
  if (a === 'post' && b === 'guide' && !c) return { name: 'post', tab: 'guide', open: null };
  if (a === 'post' && b === 'tools' && !c) return { name: 'post', tab: 'tools', open: q.get('open') };
  if (a === 'post' && b === 'accounts' && !c) return { name: 'post', tab: 'accounts', open: null };
  if (a === 'post' && b === 'tips' && !c) return { name: 'post', tab: 'tips', open: null };
  // a freelancer's email-code sign-in (owner 2026-10-18); /t/signin#t=… from the email. A link token is never 'signin'.
  if (a === 't' && b === 'signin') return { name: 'crew-signin' };
  if (a === 't' && b) return { name: 'link', token: b };
  // the model release an actor signs (no sign-in: the link is the key)
  if (a === 'sign' && b) return { name: 'sign', token: b };
  // an agreement the client signs (e-signing): the link plus a code to their email; /esign/verify checks a PDF
  if (a === 'esign' && b === 'verify') return { name: 'esign-verify' };
  if (a === 'esign' && b) return { name: 'esign', token: b };
  // a casting request the candidate answers (no sign-in: the link is the key)
  if (a === 'casting' && b) return { name: 'casting-answer', token: b };
  // the public application form for the actor library (no sign-in)
  if (a === 'apply' && !b) return { name: 'apply', ad: q.get('annons') };
  if (a === 'actors' && !b) return { name: 'talent', tab: 'library' };
  if (a === 'actors' && b === 'applications') return { name: 'talent', tab: 'applications' };
  // the client portal: /portal/{link} once, then /portal and /portal/p/{project code}; /portal/signin#t=… from the
  // sign-in email (the token stays in the fragment)
  if (a === 'portal' && b === 'r' && c) return { name: 'portal-review', code: c };
  if (a === 'portal' && b === 'p' && c) return { name: 'portal', code: c };
  if (a === 'portal' && b === 'signin') return { name: 'portal-signin' };
  if (a === 'portal' && b) return { name: 'portal-link', token: b };
  if (a === 'portal') return { name: 'portal', code: null };
  return { name: 'not-found' };
}

/** Old addresses that moved under Team: the address to show instead (same page), or null. */
export function movedPath(pathname: string, search = ''): string | null {
  const p = pathname.replace(/\/+$/, '');
  if (p === '/availability') return `/team/availability${search}`;
  if (p === '/people') return `/team/people${search}`;
  return null;
}

export function navigate(path: string): void {
  history.pushState(null, '', path);
  dispatchEvent(new PopStateEvent('popstate'));
}
